import React, { useState } from 'react';
import { Save, Play, Settings, Eye, Delete } from 'lucide-react';
import { Button } from '../ui/Button';
import { WorkflowDefinition } from '../../types/workflows';
import { WorkflowCanvas } from './canvas/WorkflowCanvas';
import { WorkflowPreview } from './WorkflowPreview';
import { apiClient } from '../../lib/api';
import { useToast } from '../../hooks/useToast';

interface WorkflowBuilderProps {
  workflow: WorkflowDefinition;
  datasetId: string;
  onSave: (workflow: WorkflowDefinition) => void;
  onCancel: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

type BuilderView = 'design' | 'preview';

export const WorkflowBuilder: React.FC<WorkflowBuilderProps> = ({
  workflow,
  datasetId,
  onSave,
  onCancel,
  isLoading,
  setIsLoading
}) => {
  const [currentWorkflow, setCurrentWorkflow] = useState<WorkflowDefinition>(workflow);
  const [currentView, setCurrentView] = useState<BuilderView>('design');
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { showToast } = useToast();

  const handleWorkflowChange = (updatedWorkflow: WorkflowDefinition) => {
    setCurrentWorkflow(updatedWorkflow);
    setIsDirty(true);
    setError(null);
  };

  const WORKFLOW_VALIDATION_RULES = [
    {
      check: (w: WorkflowDefinition) => !w.name?.trim(),
      message: 'Workflow name is required'
    },
    {
      check: (w: WorkflowDefinition) => w.name.length > 100,
      message: 'Workflow name must be less than 100 characters'
    },
    {
      check: (w: WorkflowDefinition) => !w.trigger_type,
      message: 'Workflow must have a trigger type'
    },
    {
      check: (w: WorkflowDefinition) => !w.graph?.nodes || w.graph.nodes.length === 0,
      message: 'Workflow must have at least one node'
    },
    {
      check: (w: WorkflowDefinition) => w.graph.nodes[0].config?.parameters?.dataset_id !== datasetId,
      message: 'Workflow should be connected to the selected dataset'
    }
  ];

  const validateBasicWorkflow = (workflow: WorkflowDefinition): string | null => {
    for (const rule of WORKFLOW_VALIDATION_RULES) {
      if (rule.check(workflow)) {
        return rule.message;
      }
    }
    return null;
  };

  const validateWorkflowNodes = (nodes: any[]): string | null => {
    const hasFetchLeads = nodes.some(node => node.config?.activity_name === 'FetchLeadsActivity');
    if (!hasFetchLeads) {
      return 'Workflow must start with a FetchLeadsActivity to get data from your dataset';
    }

    for (const node of nodes) {
      if (!node.node_id?.trim()) return 'All workflow nodes must have valid IDs';
      if (!node.name?.trim()) return 'All workflow nodes must have names';
      if (!node.config?.activity_name) return `Node "${node.name}" is missing its type configuration`;
    }
    return null;
  };

  const validateWorkflowEdges = (edges: any[], nodes: any[]): string | null => {
    if (!edges?.length) return null;

    for (const edge of edges) {
      if (!edge.source_node_id || !edge.destination_node_id) {
        return 'All workflow connections must have valid source and destination nodes';
      }
      
      const staticNodeIds = ['-1'];

      const sourceExists = nodes.some(n => staticNodeIds.includes(edge.source_node_id) || n.node_id === edge.source_node_id);
      const destExists = nodes.some(n => staticNodeIds.includes(edge.destination_node_id) || n.node_id === edge.destination_node_id);
      
      if (!sourceExists || !destExists) {
        return 'Workflow has invalid connections between nodes';
      }
    }

    return null;
  };

  const validateTriggerConfig = (workflow: WorkflowDefinition): string | null => {
    if (workflow.trigger_type === 'CRON' && !workflow.trigger_config?.cron) {
      return 'CRON workflows must have a valid schedule';
    }
    if (workflow.trigger_type === 'INTERVAL' && !workflow.trigger_config?.interval_seconds) {
      return 'INTERVAL workflows must have a valid time interval';
    }
    return null;
  };

  const validateWorkflow = (workflow: WorkflowDefinition): string | null => {
    return validateBasicWorkflow(workflow) ||
           validateWorkflowNodes(workflow.graph.nodes) ||
           validateWorkflowEdges(workflow.graph.edges, workflow.graph.nodes) ||
           validateTriggerConfig(workflow);
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate workflow before saving
      const validationError = validateWorkflow(currentWorkflow);
      if (validationError) {
        setError(validationError);
        setIsLoading(false);
        return;
      }

      let response;
      if (currentWorkflow.id) {
        // Update existing workflow
        response = await apiClient.updateWorkflow(currentWorkflow.id, {
          name: currentWorkflow.name,
          trigger_type: currentWorkflow.trigger_type,
          trigger_config: currentWorkflow.trigger_config,
          graph: currentWorkflow.graph,
          is_active: currentWorkflow.is_active
        });
      } else {
        // Create new workflow
        response = await apiClient.createWorkflow({
          name: currentWorkflow.name,
          trigger_type: currentWorkflow.trigger_type,
          trigger_config: currentWorkflow.trigger_config,
          graph: currentWorkflow.graph,
          is_active: currentWorkflow.is_active
        });
      }

      const savedWorkflow = {
        ...currentWorkflow,
        id: response.id,
        status: 'DRAFT' as const,
        updated_at: new Date().toISOString()
      };

      setCurrentWorkflow(savedWorkflow);
      setIsDirty(false);
      showToast('Workflow saved successfully', 'success');
      onSave(savedWorkflow);

    } catch (err: any) {
      console.error('Error saving workflow:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Unable to save your workflow';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Unable to save your workflow');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWorkflowRun = async () => {
    if (!currentWorkflow.id) {
      setError('Please save the workflow first before testing');
      return;
    }

    try {
      setIsLoading(true);
      await apiClient.executeWorkflow(currentWorkflow.id);
      showToast('Workflow run complete', 'success');
    } catch (err: any) {
      console.error('Error testing workflow:', err);
      setError('Failed to execute workflow test');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex rounded-lg border border-gray-300">
            <button
              onClick={() => setCurrentView('design')}
              className={`px-3 py-1.5 text-sm font-medium rounded-l-lg transition-colors ${
                currentView === 'design'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Design
            </button>
            <button
              onClick={() => setCurrentView('preview')}
              className={`px-3 py-1.5 text-sm font-medium rounded-r-lg transition-colors border-l border-gray-300 ${
                currentView === 'preview'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Eye className="w-4 h-4 inline mr-2" />
              Preview
            </button>
          </div>

          {isDirty && (
            <span className="text-sm text-amber-600 font-medium">
              • Unsaved changes
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {currentWorkflow.id && currentWorkflow.status === 'PUBLISHED' && (
            <Button
              size="sm"
              onClick={handleWorkflowRun}
              disabled={isLoading}
            >
              <Play className="w-3 h-3 mr-1" />
              Run
            </Button>
          )}

          <Button
            size="sm"
            onClick={handleSave}
            disabled={isLoading || !isDirty}
          >
            <Save className="w-3 h-3 mr-1" />
            Save
          </Button>

          <Button
            size="sm"
            onClick={onCancel}
            disabled={isLoading || !isDirty}
          >
            <Delete className="w-3 h-3 mr-1" />
            Discard
          </Button>

        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 mb-1">Unable to save workflow</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {currentView === 'design' ? (
          <WorkflowCanvas
            workflow={currentWorkflow}
            onChange={handleWorkflowChange}
            isLoading={isLoading}
          />
        ) : (
          <WorkflowPreview
            workflow={currentWorkflow}
          />
        )}
      </div>

      {/* Footer Info */}
      <div className="flex justify-end items-center mt-4 pt-4 border-t border-gray-200">
        <div className="text-sm text-gray-600">
          {currentWorkflow.graph?.nodes?.length || 0} nodes • {currentWorkflow.graph?.edges?.length || 0} connections
        </div>
      </div>
    </div>
  );
};